import { env } from "@/config/env";
import { createHttp, HttpError } from "@/api/http";
import { getSource, publisherKey } from "@/config/horizonscanning/sources";
import type { WizardState } from "@/config/horizonscanning/types";
import { getHorizonScanningSession, setHorizonScanningSession } from "./sessionKeys";

export interface SignupResponse {
  pendingId: string;
}

export interface ConfirmResponse {
  orgId: string;
  sessionToken: string;
  adminEmail: string;
}

export interface RecipientDto {
  email: string;
  status: string;
  invitedAt: string;
  confirmedAt?: string | null;
}

export interface WatchlistResponse {
  orgId: string;
  adminEmail: string;
  packId: string;
  packVersion: string;
  topicIds: string[];
  includeAll: boolean;
  customTopic: WizardState["customTopic"];
  publisherIds: string[];
  sourceIds?: string[];
  requestedSource: string;
  frequency: WizardState["frequency"];
  sendTime: string;
  timezone: string;
  language: string;
  persona: string;
  recipientCount: number;
  planStatus: string;
  trialStartedAt?: string | null;
  trialEndsAt?: string | null;
  digestsActive: boolean;
  effectiveFrequency: string;
  showPaywall: boolean;
  checkoutEnabled?: boolean;
  digestsSentCount?: number;
  digestsUnsubscribed?: boolean;
  recipients: RecipientDto[];
}

export function watchlistPublisherIds(data: Pick<WatchlistResponse, "publisherIds" | "sourceIds">): string[] {
  const raw = data.publisherIds?.length ? data.publisherIds : data.sourceIds ?? [];
  const seen = new Set<string>();
  const out: string[] = [];
  for (const token of raw) {
    const source = getSource(token);
    const id = source ? publisherKey(source) : token.trim();
    if (!id) continue;
    const key = id.toLowerCase();
    if (seen.has(key)) continue;
    seen.add(key);
    out.push(id);
  }
  return out;
}

export interface UnsubscribeStatus {
  kind: string;
  email: string;
  unsubscribed: boolean;
  message: string;
}

export interface HorizonScanningApi {
  signup(state: WizardState): Promise<SignupResponse>;
  confirm(token: string): Promise<ConfirmResponse>;
  getWatchlist(sessionToken: string): Promise<WatchlistResponse>;
  listRecipients(sessionToken: string): Promise<{
    adminEmail: string;
    recipients: RecipientDto[];
    cap: number;
  }>;
  inviteRecipient(sessionToken: string, email: string): Promise<WatchlistResponse>;
  removeRecipient(sessionToken: string, email: string): Promise<WatchlistResponse>;
  confirmRecipient(token: string): Promise<{ ok: boolean; email: string; message: string }>;
  markPaid(sessionToken: string): Promise<WatchlistResponse>;
  updateFrequency(sessionToken: string, frequency: WizardState["frequency"]): Promise<WatchlistResponse>;
  updateWatchlist(
    sessionToken: string,
    patch: {
      publisherIds?: string[];
      sourceIds?: string[];
      topicIds?: string[];
      includeAll?: boolean;
      customTopic?: WizardState["customTopic"];
      requestedSource?: string;
      packId?: string;
      packVersion?: string;
    },
  ): Promise<WatchlistResponse>;
  createCheckoutSession(
    sessionToken: string,
    successUrl: string,
    cancelUrl: string,
  ): Promise<{ checkoutUrl: string }>;
  confirmStripeSession(sessionId: string): Promise<WatchlistResponse>;
  submitFeedback(body: {
    orgId: string;
    score: number;
    why?: string;
    canContact: boolean;
    email?: string;
  }): Promise<{ ok: boolean; message: string }>;
  getUnsubscribeStatus(token: string): Promise<UnsubscribeStatus>;
  unsubscribe(body: { session?: string; token?: string; resume?: boolean }): Promise<UnsubscribeStatus>;
}

const SESSION_PENDING = "pending";

function createMock(): HorizonScanningApi {
  return {
    async signup(state) {
      const pendingId = crypto.randomUUID().replaceAll("-", "");
      setHorizonScanningSession(
        SESSION_PENDING,
        JSON.stringify({ pendingId, token: pendingId, state, createdAt: Date.now() }),
      );
      return { pendingId };
    },
    async confirm(token) {
      const raw = getHorizonScanningSession(SESSION_PENDING);
      if (!raw) throw new HttpError(404, "This confirm link has expired. Please start again.");
      const pending = JSON.parse(raw) as { token: string; state: WizardState };
      if (pending.token !== token) throw new HttpError(404, "This confirm link has expired. Please start again.");
      const orgId = crypto.randomUUID().replaceAll("-", "");
      const sessionToken = crypto.randomUUID().replaceAll("-", "");
      const trialEnds = new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString();
      setHorizonScanningSession(
        "org",
        JSON.stringify({
          orgId,
          sessionToken,
          adminEmail: pending.state.email,
          ...pending.state,
          recipientCount: 1,
          planStatus: "trial",
          trialStartedAt: new Date().toISOString(),
          trialEndsAt: trialEnds,
          digestsActive: true,
          effectiveFrequency: "weekly",
          showPaywall: false,
          recipients: [],
        }),
      );
      return { orgId, sessionToken, adminEmail: pending.state.email };
    },
    async getWatchlist(sessionToken) {
      const raw = getHorizonScanningSession("org");
      if (!raw) throw new HttpError(401, "Session expired.");
      const org = JSON.parse(raw) as WatchlistResponse & { sessionToken: string };
      if (org.sessionToken !== sessionToken) throw new HttpError(401, "Session expired.");
      return org;
    },
    async listRecipients(sessionToken) {
      const org = await this.getWatchlist(sessionToken);
      return { adminEmail: org.adminEmail, recipients: org.recipients ?? [], cap: 10 };
    },
    async inviteRecipient(sessionToken, email) {
      const org = await this.getWatchlist(sessionToken);
      const next: WatchlistResponse & { sessionToken: string } = {
        ...(org as WatchlistResponse & { sessionToken: string }),
        recipients: [
          ...org.recipients,
          {
            email: email.trim().toLowerCase(),
            status: "pending",
            invitedAt: new Date().toISOString(),
          },
        ],
      };
      setHorizonScanningSession("org", JSON.stringify(next));
      return next;
    },
    async removeRecipient(sessionToken, email) {
      const org = await this.getWatchlist(sessionToken);
      const next = {
        ...(org as WatchlistResponse & { sessionToken: string }),
        recipients: org.recipients.filter((r) => r.email !== email.trim().toLowerCase()),
      };
      setHorizonScanningSession("org", JSON.stringify(next));
      return next;
    },
    async confirmRecipient() {
      return { ok: true, email: "", message: "Confirmed (mock)." };
    },
    async markPaid(sessionToken) {
      const org = await this.getWatchlist(sessionToken);
      const next = {
        ...(org as WatchlistResponse & { sessionToken: string }),
        planStatus: "paid",
        digestsActive: true,
        showPaywall: false,
        effectiveFrequency: org.frequency,
      };
      setHorizonScanningSession("org", JSON.stringify(next));
      return next;
    },
    async updateFrequency(sessionToken, frequency) {
      const org = await this.getWatchlist(sessionToken);
      const paid = String(org.planStatus).toLowerCase() === "paid";
      if (!paid && frequency !== "weekly") {
        throw new HttpError(400, "Daily and bi-weekly unlock after you continue.");
      }
      const next = {
        ...(org as WatchlistResponse & { sessionToken: string }),
        frequency,
        effectiveFrequency: paid ? frequency : "weekly",
      };
      setHorizonScanningSession("org", JSON.stringify(next));
      return next;
    },
    async updateWatchlist(sessionToken, patch) {
      const org = await this.getWatchlist(sessionToken);
      const next = {
        ...(org as WatchlistResponse & { sessionToken: string }),
        ...patch,
        publisherIds: watchlistPublisherIds({
          publisherIds: patch.publisherIds ?? patch.sourceIds ?? org.publisherIds ?? org.sourceIds ?? [],
        }),
        topicIds: patch.topicIds ?? org.topicIds,
        includeAll: patch.includeAll ?? org.includeAll,
        customTopic: patch.customTopic === undefined ? org.customTopic : patch.customTopic,
      };
      setHorizonScanningSession("org", JSON.stringify(next));
      return next;
    },
    async createCheckoutSession() {
      throw new HttpError(503, "Checkout is not available in mock mode.");
    },
    async confirmStripeSession(sessionToken) {
      return this.markPaid(sessionToken);
    },
    async submitFeedback() {
      return { ok: true, message: "Thanks for your feedback. (mock)" };
    },
    async getUnsubscribeStatus() {
      return { kind: "org", email: "", unsubscribed: false, message: "Stop all digests for this watchlist." };
    },
    async unsubscribe(body) {
      return {
        kind: "org",
        email: "",
        unsubscribed: !body.resume,
        message: body.resume ? "Digests resumed." : "Digests paused.",
      };
    },
  };
}

function createRemote(baseUrl: string): HorizonScanningApi {
  const http = createHttp(baseUrl);
  return {
    signup: (state) => http.post<SignupResponse>("/horizonscanning/signup", state),
    confirm: (token) => http.get<ConfirmResponse>(`/horizonscanning/confirm?token=${encodeURIComponent(token)}`),
    getWatchlist: (sessionToken) =>
      http.get<WatchlistResponse>(`/horizonscanning/watchlist?session=${encodeURIComponent(sessionToken)}`),
    listRecipients: (sessionToken) =>
      http.get<{ adminEmail: string; recipients: RecipientDto[]; cap: number }>(
        `/horizonscanning/recipients?session=${encodeURIComponent(sessionToken)}`,
      ),
    inviteRecipient: (sessionToken, email) =>
      http.post<WatchlistResponse>("/horizonscanning/recipients/invite", { session: sessionToken, email }),
    removeRecipient: (sessionToken, email) =>
      http.post<WatchlistResponse>("/horizonscanning/recipients/remove", { session: sessionToken, email }),
    confirmRecipient: (token) =>
      http.get<{ ok: boolean; email: string; message: string }>(
        `/horizonscanning/recipients/confirm?token=${encodeURIComponent(token)}`,
      ),
    markPaid: (sessionToken) =>
      http.post<WatchlistResponse>("/horizonscanning/dev/mark-paid", { session: sessionToken }),
    updateFrequency: (sessionToken, frequency) =>
      http.post<WatchlistResponse>("/horizonscanning/frequency", { session: sessionToken, frequency }),
    updateWatchlist: (sessionToken, patch) =>
      http.post<WatchlistResponse>("/horizonscanning/watchlist/update", { session: sessionToken, ...patch }),
    createCheckoutSession: (sessionToken, successUrl, cancelUrl) =>
      http.post<{ checkoutUrl: string }>("/horizonscanning/create-checkout-session", {
        session: sessionToken,
        successUrl,
        cancelUrl,
      }),
    confirmStripeSession: (sessionId) =>
      http.post<WatchlistResponse>("/horizonscanning/stripe/confirm-session", { sessionId }),
    submitFeedback: (body) => http.post<{ ok: boolean; message: string }>("/horizonscanning/feedback", body),
    getUnsubscribeStatus: (token) =>
      http.get<UnsubscribeStatus>(`/horizonscanning/unsubscribe?token=${encodeURIComponent(token)}`),
    unsubscribe: (body) => http.post<UnsubscribeStatus>("/horizonscanning/unsubscribe", body),
  };
}

let cached: HorizonScanningApi | null = null;

export function getHorizonScanningApi(): HorizonScanningApi {
  if (cached) return cached;
  cached = env.useMockApi ? createMock() : createRemote(env.apiBaseUrl);
  return cached;
}

/**
 * Deduplicate confirm calls for the same token. React Strict Mode remounts
 * ConfirmScreen and would otherwise fire two GETs; the first deletes the
 * pending row and the second surfaces "confirm link has expired".
 */
const confirmInflight = new Map<string, Promise<ConfirmResponse>>();

export function confirmHorizonScanningToken(token: string): Promise<ConfirmResponse> {
  const key = token.trim();
  const existing = confirmInflight.get(key);
  if (existing) return existing;

  const promise = getHorizonScanningApi()
    .confirm(key)
    .catch((err) => {
      confirmInflight.delete(key);
      throw err;
    });
  confirmInflight.set(key, promise);
  return promise;
}

export { HttpError };
